// src/api/envoisBE.api.ts — SCE Issues
// « Envoyer au BE » (20/10) : une ligne dans la liste SCE_Envois, que le
// flow 6 (Power Automate) envoie en carte Teams et/ou en mail. L'appli n'a ni
// serveur ni droit d'envoi : comme la NC-S (fichier déposé → flow 5), elle
// écrit, le flow envoie (docs/flows/flow-6-envoi-be.md).
//
// La liste est adressée par son NOM sur le site de l'appli (Graph l'accepte à
// la place de l'identifiant) : aucune variable d'environnement à ajouter.

import { GraphApiError, graphGet, graphPost } from './graphClient'
import { ligneEnvoi, type LigneEnvoi } from '@/utils/pageBE'
import { ecrireBlocPrefs, lireBlocPrefs } from './userPrefs.api'
import { deposerNotificationsAppli } from './cloche.api'
import { lienProbleme } from '@/utils/liensFiches'
import {
  champsEnvoi,
  CLE_BLOC_ENVOIS,
  lireFrequents,
  noterEnvoi,
  type Destinataire,
  type DestinataireFrequent,
  type EnvoiADeposer,
} from '@/utils/envoiBE'

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
export const NOM_LISTE_ENVOIS = 'SCE_Envois'

/**
 * Écrit la demande d'envoi ; le flow 6 se déclenche à sa création. Puis la
 * cloche de chaque destinataire (03/11), au mieux : la seule trace pour qui a
 * réglé « l'appli seulement ».
 */
export async function deposerEnvoi(envoi: EnvoiADeposer): Promise<void> {
  await deposerLigneEnvoi(champsEnvoi(envoi))
  const { issue, expediteur, note, destination } = envoi.contenu
  await deposerNotificationsAppli(
    envoi.destinataires
      .filter((d) => d.upn.trim().toLowerCase() !== envoi.expediteurUpn.trim().toLowerCase())
      .map((d) => ({
        destinataire: d.upn,
        reference: issue.id,
        genre: 'be' as const,
        auteur: expediteur,
        auteurUpn: envoi.expediteurUpn,
        quoi: 'vous envoie',
        libelle: issue.titre,
        extrait: note,
        raison: `Envoyé ${destination}`,
        lien: lienProbleme(issue.id),
      })),
  )
}

/**
 * Une ligne de SCE_Envois déjà composée : le flow 6 l'envoie telle quelle. Sert
 * aussi aux notifications des FNC-T (utils/notificationsFnct, lot F6).
 */
export async function deposerLigneEnvoi(champs: Record<string, string>): Promise<void> {
  await graphPost<unknown>(`/sites/${SITE_ID}/lists/${NOM_LISTE_ENVOIS}/items`, { fields: champs })
}

interface PageEnvois {
  value: { createdDateTime: string; fields: Record<string, unknown> }[]
  '@odata.nextLink'?: string
}

/**
 * Tous les envois de problèmes (page « Pour le BE », 24/10) : la liste entière,
 * petite (une ligne par envoi), filtrée ici — Destinataires est un texte
 * multiligne, que Graph ne sait pas filtrer. Les notifications FNC-T sont écartées.
 */
export async function listerEnvoisProblemes(): Promise<LigneEnvoi[]> {
  const lignes: LigneEnvoi[] = []
  let suivante: string | null =
    `/sites/${SITE_ID}/lists/${NOM_LISTE_ENVOIS}/items?$expand=fields($select=Title,Destinataires,Expediteur,Note)&$select=id,createdDateTime&$top=500`
  while (suivante) {
    const page: PageEnvois = await graphGet<PageEnvois>(suivante)
    for (const l of page.value) {
      const e = ligneEnvoi(l.fields, l.createdDateTime)
      if (e) lignes.push(e)
    }
    suivante = page['@odata.nextLink'] ?? null
  }
  return lignes
}

/** Les personnes à qui `upn` envoie le plus (bloc envoisBE de ses Prefs) ; [] si illisible. */
export async function lireDestinatairesFrequents(upn: string): Promise<DestinataireFrequent[]> {
  try {
    return lireFrequents(await lireBlocPrefs(upn, CLE_BLOC_ENVOIS))
  } catch (err) {
    console.warn('[envoisBE] envois fréquents illisibles :', err)
    return []
  }
}

/** Note un envoi à `personnes` (relu avant écriture). Au mieux : un échec ne gêne pas l'envoi. */
export async function noterDestinataires(upn: string, personnes: readonly Destinataire[]): Promise<void> {
  if (!upn || personnes.length === 0) return
  try {
    const bloc = await lireBlocPrefs(upn, CLE_BLOC_ENVOIS)
    await ecrireBlocPrefs(upn, CLE_BLOC_ENVOIS, noterEnvoi(bloc, personnes, new Date()))
  } catch (err) {
    console.warn('[envoisBE] envois fréquents non notés :', err)
  }
}

/** Message à montrer quand l'écriture échoue. */
export function messageErreurEnvoi(err: unknown): string {
  if (err instanceof GraphApiError && err.status === 404) {
    return `La liste ${NOM_LISTE_ENVOIS} n’existe pas encore sur le site : à créer (docs/flows/flow-6-envoi-be.md).`
  }
  if (err instanceof GraphApiError && err.status === 400) {
    return `La liste ${NOM_LISTE_ENVOIS} refuse la demande : une colonne manque ou n’a pas le bon nom (docs/flows/flow-6-envoi-be.md).`
  }
  return err instanceof Error ? `Envoi impossible : ${err.message}` : 'Envoi impossible.'
}
